// @flow strict

import React from 'react';
import { injectIntl } from 'react-intl';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';

// Constants
import { LegacyTheme, InputNumber, RadioButtonSet } from 'components/_ReactUI_V1';

import {
    SENSITIVITY_OPTIONS,
    SENSITIVITY_OPTION_LIMITS,
    NUMBER_INPUT_PLACEHOLDER,
    STYLE_VALUES,
} from 'utils/constants';

// Helpers
import { clamp } from 'utils/helpers';

// Styles
import {
    RangeWrapper,
    RangeTitle,
    RangeInputWrapper,
    RangeInput,
    VariableBodySection,
    VariableTitle,
} from './ThreeDSensitivitySidebarSection/styles';

// Components

import { selectDatasetReagentInfo } from 'services/Dataset/selectors';

// Types
import type { IntlType, InputEvent } from 'types';
import { SensitivityOption } from 'containers/CircuitComputationContainer/ThreeDSensitivityContainer';
import type { ReagentInfoForDataset } from 'services/Dataset/types';

type VariableType = 'X' | 'Y';

type Props = {
    intl: IntlType,

    mode: '2D' | '3D',
    sensitivityOptionAvailability: SensitivityOptionsAvailability,

    variableKey: VariableType,

    selectedVariableX: ?SensitivityOption,
    startX: ?number,
    finishX: ?number,
    // Not available in 2D mode:
    selectedVariableY: ?SensitivityOption,
    startY: ?number,
    finishY: ?number,

    onVariableChange: (newState: {
        selectedVariableX?: ?SensitivityOption,
        startX?: ?number,
        finishX?: ?number,
        selectedVariableY?: ?SensitivityOption,
        startY?: ?number,
        finishY?: ?number,
    }) => void,

    /**
     * The reagent limits for the dataset's reagent
     */
    reagentInfo: ?ReagentInfoForDataset,
};

const INPUT_STYLES = {
    ...STYLE_VALUES.DEFAULT_SMALL_INPUT_NUMBER_STYLES,
    marginRight: '0px',
};

/**
 * The sidebar body section for the 3D sensitivity plot.
 */
class SensitivityVariableOptions extends React.PureComponent<Props, State> {

    getOptionLabel = (option: SensitivityOption) => {
        if (option === SENSITIVITY_OPTIONS.REAGENT_CONCENTRATION) {
            if (this.props.reagentInfo) {
                const name = this.props.reagentInfo.get('name');

                const unit = this.props.reagentInfo.has('oximeId') ? 'vol. %' : 'g/L';

                if (name) {
                    return `${name} ${unit}`;
                }
            }
        }

        return this.props.intl.formatMessage({
            id: `constants.SensitivityOptions.${option}`,
        });
    }

    getOptionsFilteredByAvailability = () => {
        return Object.keys(SENSITIVITY_OPTIONS)
            .filter((optionKey: string) =>
                Object.keys(this.props.sensitivityOptionAvailability).indexOf(optionKey) !== 1
                    ? this.props.sensitivityOptionAvailability[optionKey]
                    : true
            );
    }

    getOptions = () => {
        let reagentType = null;
        if (!this.props.isFetchingReagentInfo && this.props.reagentInfo) {
            // Only reagents have a reagent id
            reagentType = this.props.reagentInfo.get('type');
        }
        const allOptions = Object.keys(SENSITIVITY_OPTIONS);
        return allOptions.filter((option: SensitivityOption) => {
            if (!reagentType && option === SENSITIVITY_OPTIONS.REAGENT_CONCENTRATION) {
                return false;
            }
            // If the dataset does not use an oxime, then the oxmod cannot be used.
            if (reagentType !== 'OXIME' && option === SENSITIVITY_OPTIONS.OXMOD_RATIO) {
                return false;
            }
            return true;
        });
    }

    getOptionMin = (option: SensitivityOption) => {
        if (!this.props.isFetchingDatasetReagentInfo && this.props.reagentInfo) {
            if (option === SENSITIVITY_OPTIONS.REAGENT_CONCENTRATION) {
                return this.props.reagentInfo.get('minimumConcentration');
            }
            if (option === SENSITIVITY_OPTIONS.OXMOD_RATIO) {
                if (this.props.reagentInfo.has('minimumOximeModifier')) {
                    return this.props.reagentInfo.get('minimumOximeModifier');
                }
            }
        }

        return SENSITIVITY_OPTION_LIMITS[option].MINIMUM;
    };

    getOptionMax = (option: SensitivityOption) => {
        if (option === SENSITIVITY_OPTIONS.REAGENT_CONCENTRATION) {
            if (!this.props.isFetchingDatasetReagentInfo && this.props.reagentInfo) {
                return this.props.reagentInfo.get('maximumConcentration');
            }
            if (option === SENSITIVITY_OPTIONS.OXMOD_RATIO) {
                if (this.props.reagentInfo.has('maximumOximeModifier')) {
                    return this.props.reagentInfo.get('maximumOximeModifier');
                }
            }
        }

        return SENSITIVITY_OPTION_LIMITS[option].MAXIMUM;
    };

    /**
     * Gets the radio button options for the variable to vary
     */
    getVariableRadioOptions = (variableKey: VariableType) =>
        this.getOptions().map((value: string) => ({
            value,
            label: this.getOptionLabel(value),
            // Disable the input if the opposite variable of that type has been selected.
            disabled: this.props[`selectedVariable${variableKey === 'X' ? 'Y' : 'X'}`] === value,
        }));

    getVariable = (variableKey: VariableType) => this.props[`selectedVariable${variableKey}`];

    getVariableMin = (variableKey: VariableType) => {
        const variable = this.getVariable(variableKey);
        if (!variable) return null;

        return this.getOptionMin(variable);
    };

    getVariableMax = (variableKey: VariableType) => {
        const variable = this.getVariable(variableKey);
        if (!variable) return null;

        return this.getOptionMax(variable);
    };

    /**
     * Handle radio button change
     */
    handleVariableSelected = (variableKey: VariableType) => (selectedVariable: SensitivityOption) =>
        this.props.onVariableChange({
            [`selectedVariable${variableKey}`]: selectedVariable,
            [`start${variableKey}`]: this.getOptionMin(selectedVariable),
            [`finish${variableKey}`]: this.getOptionMax(selectedVariable),
        });

    /**
     * Handle the change in range start or finish.
     */
    handleRangeChange = (variableKey: VariableType, inputKey: 'start' | 'finish') => (
        event: InputEvent
    ) => {
        const stateKey = [`${inputKey}${variableKey}`];
        if (event.target.value === '') {
            // the pressed backspace.
            this.props.onVariableChange({
                [stateKey]: null
            });
            return;
        }
        this.props.onVariableChange({ [stateKey]: event.target.value });
    };

    handleOnBlur = (variableKey: VariableType, inputKey: 'start' | 'finish') => (
        event: InputEvent
    ) => {
        const stateKey = [`${inputKey}${variableKey}`];
        if (event.target.value === '') {
            // the pressed backspace.
            this.setState({ [stateKey]: null });
            return;
        }
        const min = this.getVariableMin(variableKey);
        const max = this.getVariableMax(variableKey);

        const value = clamp(
            Number(event.target.value), min, max
        );
        this.props.onVariableChange({ [stateKey]: value });
    };


    render() {
        return (
            <VariableBodySection>
                <VariableTitle>
                    <span>
                        {this.props.intl.formatMessage({
                            id: `components.CircuitComputationSidebar.${this.props.mode}Sensitivity.variable${this.props.variableKey}.title`,
                        })}
                    </span>
                </VariableTitle>
                <RadioButtonSet
                    value={this.props[`selectedVariable${this.props.variableKey}`]}
                    options={this.getVariableRadioOptions(this.props.variableKey)}
                    onClick={this.handleVariableSelected(this.props.variableKey)}
                    disabled={this.props.loading}
                    styles={{
                        labelColor: LegacyTheme.defaultColor,
                        labelFontSize: '13px',
                    }}
                />
                <RangeWrapper>
                    <RangeTitle>
                        <span>
                            {this.props.intl.formatMessage({
                                id: `components.CircuitComputationSidebar.${this.props.mode}Sensitivity.range${this.props.variableKey}.title`,
                            })}
                        </span>
                    </RangeTitle>
                    <RangeInputWrapper>
                        <RangeInput>
                            <span>
                                {this.props.intl.formatMessage({
                                    id: `components.CircuitComputationSidebar.${this.props.mode}Sensitivity.range${this.props.variableKey}.startLabel`,
                                })}
                            </span>
                            <InputNumber
                                handleOnChange={this.handleRangeChange(this.props.variableKey, 'start')}
                                onBlur={this.handleOnBlur(this.props.variableKey, 'start')}
                                value={this.props[`start${this.props.variableKey}`] || ''}
                                placeholder={NUMBER_INPUT_PLACEHOLDER}
                                disabled={this.props.loading}
                                textAlign="center"
                                min={this.getVariableMin(this.props.variableKey)}
                                max={this.getVariableMax(this.props.variableKey)}
                                style={INPUT_STYLES}
                            />
                        </RangeInput>
                        <RangeInput>
                            <span>
                                {this.props.intl.formatMessage({
                                    id: `components.CircuitComputationSidebar.${this.props.mode}Sensitivity.range${this.props.variableKey}.endLabel`,
                                })}
                            </span>
                            <InputNumber
                                handleOnChange={this.handleRangeChange(this.props.variableKey, 'finish')}
                                onBlur={this.handleOnBlur(this.props.variableKey, 'finish')}
                                value={this.props[`finish${this.props.variableKey}`] || ''}
                                placeholder={NUMBER_INPUT_PLACEHOLDER}
                                disabled={this.props.loading}
                                textAlign="center"
                                min={this.getVariableMin(this.props.variableKey)}
                                max={this.getVariableMax(this.props.variableKey)}
                                style={INPUT_STYLES}
                            />
                        </RangeInput>
                    </RangeInputWrapper>
                </RangeWrapper>
            </VariableBodySection>
        );
    }
}

const mapStateToProps = () =>
    createStructuredSelector({
        reagentInfo: selectDatasetReagentInfo(),
    });

const mapDispatchToProps = (dispatch: ReduxDispatch) =>
    bindActionCreators(
        {
        },
        dispatch
    );


export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(SensitivityVariableOptions));
